import React, { useState, useEffect, useCallback } from 'react';
import { Box, Divider, Grid, Typography, useMediaQuery, Button, IconButton, Autocomplete, TextField } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import CircularProgress from '@mui/material/CircularProgress';
import WhiteInosio from '../../../icons/whiteinosio.svg';
import { selectApplicationsState, setApplicantPropertyDetails } from '../sagas/pm/applicationsSlice';
import { applicantStyles } from './CustomStyles';
import { ApplicantPageStates } from '../Applicant';
import BackIcon from '../../../icons/back1.svg';
import '../../../App.css';
import { getSearchingProperty, selectInvitationState } from '../../../shared/signup/sagas/invitations/invitationSlice';
import debounce from 'lodash.debounce';
import { selectPropertyGroupDetailsState } from '../../../shared/sagas/propertyGroups/propertyGroupsSlice';

interface PropsType {
  setPageState: (pageState: ApplicantPageStates) => void;
}

class PropertyAddress {
  Uuid!: string;
  Label!: string;
}

export default function ApplicantRegistration(props: PropsType) {
  const dispatch = useDispatch();
  const classes = applicantStyles();

  const matches560 = useMediaQuery('(max-width:560px)');
  const matches900 = useMediaQuery('(max-width:900px)');

  const { invitedApplicationResponse } = useSelector(selectApplicationsState);
  const { propertyGroupDetails: PropertyGroupDetails } = useSelector(selectPropertyGroupDetailsState);
  const [propertySearchOptions, setPropertySearchOptions] = React.useState<readonly PropertyAddress[]>([]);
  const { isSearchingProperty, propertyResponse } = useSelector(selectInvitationState);
  const [propertyAddress, setPropertyAddress] = useState(invitedApplicationResponse?.Application ? `Co-applicant for ${invitedApplicationResponse.Application.Uuid}` : ''); //TODO: we may want to change this.
  const [locationUuid, setLocationUuid] = useState<string|null|undefined>(null);

  const handleGoBack = () => {
    props.setPageState(ApplicantPageStates.Landing);
  };

  useEffect(() => {
    setPropertySearchOptions(
      propertyResponse.properties.map((p) => {
        return {
          Uuid: p.Uuid,
          Label: p.Address.Line1 || '',
        };
      }),
    );
  }, [propertyResponse]);

  const actuallySearchPropertiesByAddress = (addressString: string) => {
    if (!!addressString) {
      dispatch(
        getSearchingProperty({
          PropertyName: undefined,
          PhysicalAddress: addressString,
          Country: 'US',
          PropertyGroupUuid: PropertyGroupDetails.Uuid,
        }),
      );
    }
  };

  const delayedAddressSearch = useCallback(debounce(actuallySearchPropertiesByAddress, 5), [propertyAddress]);//put the debounce to 5ms to essesntially remove it for now.

  useEffect(() => {
    delayedAddressSearch(propertyAddress);
    return delayedAddressSearch.cancel;
  }, [propertyAddress, delayedAddressSearch]);

  const handleContinue = () => {
    dispatch(setApplicantPropertyDetails({ propertyAddress, locationUuid }));

    if (PropertyGroupDetails?.ApplicationsSettings?.ApplicationsPolicyText) {
      props.setPageState(ApplicantPageStates.ApplicationPolicies);//there are app policies, send them there.
    } else if (PropertyGroupDetails?.ApplicationsSettings?.ApplicationsPetPolicyText) {
      props.setPageState(ApplicantPageStates.PetPolicies);//there are pet policies, send them there.
    } else {
      props.setPageState(ApplicantPageStates.Registration);//no policies. Send them to registration.
    }
  }

  return (
    <Box sx={{ backgroundColor: '#F1F0F5', width: '100vw', height: '100vh', textAlign: 'center' }}>
      <Box sx={{ backgroundColor: '#ffffff', height: 93, display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: '35px' }}>
        <img src={WhiteInosio} style={{ width: '65px', height: '65px' }} alt="logo" />
        <Button href="/login" className={classes.signInBtn}>
          <Typography className={classes.signInText}>Sign Up | Sign In</Typography>
        </Button>
      </Box>

      {!matches900 && (
        <Typography className={classes.largeText} sx={{ mt: 4, textAlign: 'center' }}>
          {PropertyGroupDetails?.CompanyName ?? ''} Application Portal
        </Typography>
      )}

      <Box sx={{ display: 'flex', backgroundColor: '#F1F0F5', flexDirection: 'column', alignItems: 'center', mt: matches900 ? 8 : 4 }}>
        <Box className={classes.formContainer} sx={{ width: '100%', maxWidth: 472, padding: '30px 0 40px', backgroundColor: matches560 ? '#F1F0F5' : '#ffffff' }}>
          <Box sx={{ position: 'relative', width: '100%' }}>
            <IconButton
              edge="start"
              color="inherit"
              aria-label="go to prev"
              onClick={handleGoBack}
              sx={{
                position: 'absolute',
                left: matches560 ? 20 : 30,
                top: '-8px',
              }}
            >
              <img src={BackIcon} alt="back" style={{ cursor: 'pointer' }} />
            </IconButton>
            <Typography className={classes.largeText} sx={{ mb: 4.5, textAlign: 'center', fontSize: matches560 ? '16px !important' : '18px' }}>
              Which property are you applying for?
            </Typography>
          </Box>

          <Box sx={{ maxWidth: '340px' }}>
            {!invitedApplicationResponse.Application && (
              <>
                <Typography
                  sx={{
                    fontWeight: 400,
                    fontSize: "16px",
                    lineHeight: "22px",
                    fontFamily: "Work Sans",
                    color: '#000000',
                    mb: 6,
                  }}>
                  Start typing the address of the property you want to apply for and select it from the list.
                </Typography>
                <Autocomplete
                  freeSolo={true}
                  noOptionsText="No locations found"
                  id="propertyAddressSearchBox"
                  sx={{
                    '& .MuiAutocomplete-inputRoot': {
                      borderRadius: '24px !important',
                      height: '49px  !important',
                      marginBottom: '16px !important',
                      boxShadow: '0px 0px 40px 0px rgba(0, 0, 0, 0.1) !important',
                      outline: 'none !important',
                      width: '100%',
                    },
                  }}
                  onChange={(event, value) => {
                    setPropertyAddress((value as any)?.Label ?? value);
                    setLocationUuid((value as any)?.Uuid ?? null);
                  }}
                  onInputChange={(event, value) => {
                    setPropertyAddress(value);
                  }}
                  isOptionEqualToValue={(option, value) => option.Label === value.Label}
                  getOptionLabel={(option) => {
                    return (option as any).Label ?? option;
                  }}
                  options={propertySearchOptions}
                  loading={!!isSearchingProperty}
                  renderInput={(params) => (
                    <TextField
                      ref={params.InputProps.ref}
                      {...params}
                      placeholder="Start typing to select your new home"
                      label="Desired Property Address"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {isSearchingProperty ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </React.Fragment>
                        ),
                      }}
                    />
                  )}
                />
              </>
            )}

            <Box className={classes.buttonContainer} sx={{ width: '100%', justifyContent: 'flex-end', mt: 15 }}>
              <Box className={classes.wrapper}>
                <Button
                  type="submit"
                  variant="contained"
                  style={{
                    width: '113px',
                    height: '49px',
                    borderRadius: '24px',
                    textTransform: 'capitalize',
                    color: 'white',
                    backgroundColor: !!propertyAddress ? "#39A2DB": "#D3D3D3",
                    borderColor: !!propertyAddress ? "#39A2DB": "#D3D3D3",
                    fontFamily: 'Open Sans',
                    fontWeight: '700',
                    fontSize: '18px',
                    lineHeight: '22px',
                  }}
                  onClick={handleContinue}
                  disabled={!propertyAddress}
                  id="Sign-Up"
                >
                  Continue
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
        {matches900 ? (
          <></>
        ) : (
          <Grid item sx={{ width: '100%', maxWidth: 523, mt: 9 }}>
            <Divider />
            <Grid item display="flex" justifyContent="center">
              <span
                className={classes.forgetText}
                onClick={() => {
                  window.open('http://support.inosio.com/');
                }}
              >
                {'Need help?'}
              </span>
            </Grid>
          </Grid>
        )}
      </Box>
    </Box>
  );
}
